import {containersSelector, useGetContainersByWellQuery, WellEntity} from "@ots-frontend/ots-data";
import {ActionIcon, Anchor, Card, Container, Divider, Grid, Group, Modal, Text, Title} from "@mantine/core";
import {useTranslation} from "react-i18next";
import {ObjectMappingDataTableCard} from "../../card/ObjectMappingDataTableCard/ObjectMappingDataTableCard";
import {Link} from "react-router-dom";
import {DefaultText} from "../../text/DefaultText/DefaultText";
import {HasAccess} from "../../auth/HasAccess/HasAccess";
import {ContainerDataTable} from "../../data-table/ContainerDataTable/ContainerDataTable";
import {useMemo} from "react";
import {skipToken} from "@reduxjs/toolkit/query";
import {useApp} from "../../../providers/RegionProvider/RegionProvider";
import {RestrictionDataTableCard} from "../../card/RestrictionDataTableCard/RestrictionDataTableCard";
import {
  RrcLocationProfileDataTableCard
} from "../../card/RrcLocationProfileDataTableCard/RrcLocationProfileDataTableCard";
import {
  RrcEp9WasteProfileDataTableCard
} from "../../card/RrcEp9WasteProfileDataTableCard/RrcEp9WasteProfileDataTableCard";
import {EntityGraphView} from "../../graph/EntityGraphView/EntityGraphView";
import {IconHierarchy} from "@tabler/icons-react";
import {useDisclosure} from "@mantine/hooks";

export type WellDetailsProps = {
  well: WellEntity
}

export const WellDetails = (props: WellDetailsProps) => {
  const {well} = props;

  const {t} = useTranslation(["common"]);
  const {regionId, compliance} = useApp();

  const [opened, {close, open}] = useDisclosure(false);

  const selectContainers = useMemo(() => {
    return containersSelector();
  }, []);

  const {containers, isContainersLoading} = useGetContainersByWellQuery(regionId ? {
    regionId: regionId,
    wellId: well.id,
  } : skipToken, {
    selectFromResult: (result) => ({
      containers: selectContainers(result, {showDoNotUse: true}),
      isContainersLoading: result.isLoading
    })
  });

  return (
    <Grid>
      <Grid.Col span={6}>
        <Card shadow="sm" padding="lg" radius="md" withBorder>

          <Group position="apart" pb="sm">
            <Title order={4}>{t("profile.well.info.section.details.label", "Details")}</Title>
            <ActionIcon onClick={() => open()}>
              <IconHierarchy size="1.125rem"/>
            </ActionIcon>
          </Group>

          <Divider/>

          <Grid>
            <Grid.Col span={6}>
              <Text ta="left" fz="sm" weight={500} mt="md">
                {t("profile.well.info.field.name.label", "Name")}
              </Text>
              <Text ta="left" c="dimmed" fz="xs">
                {well.name}
              </Text>
            </Grid.Col>
            <Grid.Col span={6}>
              <Text ta="left" fz="sm" weight={500} mt="md">
                {t("profile.well.info.field.afe.label", "AFE")}
              </Text>
              <Text ta="left" c="dimmed" fz="xs">
                {well.afe &&
                  <>
                    {well.afePrefix && well.afePrefix}
                    <Text fw={700} inherit={true} span={true}>
                      {` ${well.afe} `}
                    </Text>
                    {well.afeSuffix && well.afeSuffix}
                  </>
                }
              </Text>
            </Grid.Col>
            <Grid.Col span={12}>
              <Text ta="left" fz="sm" weight={500} mt="md">
                {t("profile.well.info.field.site.label", "Site")}
              </Text>
              <Text ta="left" c="dimmed" fz="xs">
                {well.siteId &&
                  <Anchor component={Link} to={`/sites/profile?id=${well.siteId}`}>
                    <DefaultText value={well.siteName}/>
                  </Anchor>
                }
              </Text>
            </Grid.Col>
            <Grid.Col span={6}>
              <Text ta="left" fz="sm" weight={500} mt="md">
                {t("profile.well.info.field.leaseid.label", "Lease ID")}
              </Text>
              <Text ta="left" c="dimmed" fz="xs">
                {well.leaseId}
              </Text>
            </Grid.Col>
            <Grid.Col span={6}>
              <Text ta="left" fz="sm" weight={500} mt="md">
                {t("profile.well.info.field.leasename.label", "Lease Name")}
              </Text>
              <Text ta="left" c="dimmed" fz="xs">
                {well.leaseName}
              </Text>
            </Grid.Col>
            <Grid.Col span={6}>
              <Text ta="left" fz="sm" weight={500} mt="md">
                {t("profile.well.info.field.usable.label", "Usable")}
              </Text>
              <Text ta="left" c={well.doNotUse ? "warning" : "dimmed"} fz="xs">
                {well.doNotUse ? t("common.shared.no", "No") : t("common.shared.yes", "Yes")}
              </Text>
            </Grid.Col>
          </Grid>
        </Card>
      </Grid.Col>

      <HasAccess permissions={["object_mapping_view"]}>
        <Grid.Col span={6}>
          <ObjectMappingDataTableCard entity={well}/>
        </Grid.Col>
      </HasAccess>

      <Grid.Col span={12}>
        <Card shadow="sm" padding="lg" radius="md" withBorder>

          <Group position="apart" pb="sm">
            <Title order={4}>
              {t("common.entity.container.plural", "Containers")}
            </Title>
          </Group>

          <Divider pb="sm"/>

          <ContainerDataTable
            withBorder
            records={containers}
            fetching={isContainersLoading}
          />
        </Card>
      </Grid.Col>

      <HasAccess permissions={["restriction_view"]}>
        <Grid.Col span={12}>
          <RestrictionDataTableCard entity={well}/>
        </Grid.Col>
      </HasAccess>

      {compliance?.rrc === true &&
        <>
          <HasAccess permissions={["rrclocationprofile_view"]}>
            <Grid.Col span={12}>
              <RrcLocationProfileDataTableCard entity={well}/>
            </Grid.Col>
          </HasAccess>
          <HasAccess permissions={["rrcep9wasteprofile_view"]}>
            <Grid.Col span={12}>
              <RrcEp9WasteProfileDataTableCard entity={well}/>
            </Grid.Col>
          </HasAccess>
        </>
      }

      {opened &&
        <Modal opened={opened} onClose={close} size="auto" title="Entity Graph">
          <Container h={700} w={800}>
            <EntityGraphView entity={well}/>
          </Container>
        </Modal>
      }
    </Grid>
  );
};

